import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Collapse, Input, Tree, Tooltip, Row, Col, Button, Space, Alert } from 'antd';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SearchOutlined, PlusOutlined, WarningOutlined, RocketOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { usePrimeModel } from '../../contexts/PrimeModelContext';
import { PrimeStateManager } from './PrimeStateManager';
import { StatePropertyManager } from './StatePropertyManager';
import { PrimeArtifactPropertiesManager } from './PrimeArtifactPropertiesManager';
import PrimeArtifactDetails from './PrimeArtifactDetails/';
import { WorkflowStatusComponent } from '../CompositeCards/ComponentRegistry/WorkflowStatusComponent';
import { TransitionProcessGenerator } from './TransitionProcessGenerator';
import { TransitionsProcessesView } from './TransitionsProcessesView';
import { ProcessArtifactManager } from './ProcessArtifactManager';
import { ArtifactStateFlowDiagram } from './ArtifactStateFlowDiagram';
import { CreationRelationshipsManager } from './CreationRelationshipsManager';
import { TabValidationStatus } from './TabValidationStatus';
import { ApprovalToggle } from './ApprovalToggle';
import { EditProcessDialog } from './EditProcessDialog';
import { PrimeModelTester } from './PrimeModelTester';
import { useTabValidation } from '../../hooks/useTabValidation';
import { PrimeAgentAssignment } from './PrimeAgentIntegration/PrimeAgentAssignment';
import { AgentRunList } from './PrimeAgentIntegration/AgentRunList';
import { AgentRunDetails } from './PrimeAgentIntegration/AgentRunDetails';
import { PrimeAgentChat } from './PrimeAgentIntegration/PrimeAgentChat';
import { useUser } from '../../contexts/UserContext';
import { PERMISSIONS } from '../../constants/permissions';
import { StateDetailEditor, TransitionDetailEditor, ProcessDetailEditor, ArtifactDetailEditor } from './Editors';
import ArtifactCreationDialog from './ArtifactCreationDialog';
import { deletePrimeArtifact } from '../../store/actions/primeArtifactActions';
import styles from './PrimeModelManager.module.css';
import { PrimeArtifact } from '../../types/primeArtifact';
import { PrimeProcess, PrimeArtifactState } from '../../types/primeModel';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { apiClient } from '../../services/apiClient';
import { KrybotService } from '../../services/api/krybotService';
import { ArtifactStateTransitionType, DemandType, DemandFlowType } from '../../types/enums';
import type { KrybotAgentRun } from '../../types/krybot';
import { PrimeArtifactStateCreate } from '../../types/primeArtifactState';
import { fetchPrimeArtifactsHierarchy } from '../../store/actions/primeArtifactActions';
import { AppDispatch, RootState } from '../../store';

const { Search } = Input;

interface PrimeModelManagerProps {
  _artifact: PrimeArtifact;
}

const PrimeModelManager: React.FC<PrimeModelManagerProps> = React.memo(({ _artifact }) => {
  // Removed verbose component render log

  const { selectArtifact } = usePrimeModel();
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated, isLoading: userLoading, hasPermission } = useUser();

  // Manage artifact data locally to include fetched states/transitions/processes
  const [localArtifact, setLocalArtifact] = useState<PrimeArtifact>(_artifact);
  const currentArtifact = localArtifact;
  const [selectedState, setSelectedState] = useState<PrimeArtifactState | null>(null);
  
  // Tab validation hook
  const { 
    validation: tabValidation, 
    approveDetails, 
    approveStates, 
    approveTransitions, 
    approveProcesses 
  } = useTabValidation(currentArtifact.id);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProcess, setSelectedProcess] = useState<PrimeProcess | null>(null);
  const [editingProcessId, setEditingProcessId] = useState<string | null>(null);
  const [showEditProcessDialog, setShowEditProcessDialog] = useState(false);
  const [selectedAgentRun, setSelectedAgentRun] = useState<string | null>(null);
  const [selectedAgentRunData, setSelectedAgentRunData] = useState<KrybotAgentRun | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const isFetching = useRef(false);
  
  // Artifact tree state
  const [artifactSearchTerm, setArtifactSearchTerm] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Load saved width from localStorage, default to 250
    const saved = localStorage.getItem('primeModelSidebarWidth');
    return saved ? parseInt(saved, 10) : 250;
  });
  const [isResizing, setIsResizing] = useState(false);
  
  // Diagram collapse state
  const [diagramCollapsed, setDiagramCollapsed] = useState(() => {
    // Load saved collapse state from localStorage, default to false (expanded)
    const saved = localStorage.getItem('primeModelDiagramCollapsed');
    return saved === 'true';
  });
  const artifactsHierarchy = useSelector((state: RootState) => state.primeArtifacts.primeArtifacts);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'root' | 'child' | 'peer'>('root');
  const [dialogParent, setDialogParent] = useState<{ id: string | null; name: string | null }>({ id: null, name: null });
  
  // Diagram selection state
  const [selectedElement, setSelectedElement] = useState<{ type: 'artifact' | 'state' | 'transition' | 'process'; id: string }>({ type: 'artifact', id: currentArtifact.id });
  const [statesForDiagram, setStatesForDiagram] = useState<any[]>([]);
  const [transitionsForDiagram, setTransitionsForDiagram] = useState<any[]>([]);
  const [visibleTransitionTypes, setVisibleTransitionTypes] = useState<string[]>(['PROCESSITEM', 'PROCESSITEM_PERSISTENT', 'MATERIAL', 'RESOURCE', 'CREATION']);
  
  // Add State form state
  const [showAddState, setShowAddState] = useState(false);
  const [newStateName, setNewStateName] = useState('');
  const [newStateDescription, setNewStateDescription] = useState('');
  const [newStateType, setNewStateType] = useState<ArtifactStateTransitionType>(ArtifactStateTransitionType.OPERATIONAL);
  const [newStateIsInitial, setNewStateIsInitial] = useState(false);
  const [newStateIsFinal, setNewStateIsFinal] = useState(false);
  const [newStateIsRequired, setNewStateIsRequired] = useState(true);

  // Handle reset to template
  const handleResetToTemplate = async () => {
    if (!currentArtifact.artifact_type_id) {
      alert('This artifact is not linked to an artifact type. Cannot reset to template.');
      return;
    }

    const confirmed = window.confirm(
      'This will DELETE all existing states and transitions for this artifact and replace them with the default template from the artifact type.\n\nThis action cannot be undone.\n\nAre you sure you want to continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post(`/prime-artifacts/${currentArtifact.id}/apply-template`);
      
      // Refresh the artifact data to get the new states and transitions
      await Promise.all([
        fetchStatesRef.current(localArtifact),
        fetchTransitionsRef.current(localArtifact),
      ]);
      
      alert(`Template applied successfully! Created ${response.data.states_created} states and ${response.data.transitions_created} transitions.`);
    } catch (error: any) {
      console.error('Error applying template:', error);
      alert(error.response?.data?.detail || 'Failed to apply template');
    } finally {
      setLoading(false);
    }
  };

  // Handle apply default states
  const handleApplyDefaultStates = async () => {
    const confirmed = window.confirm(
      'This will add default operational and improvement states to this artifact.\n\nExisting states will be preserved.\n\nOperational: Initialized → Created → Drafted → Active → Closed\nImprovement: Identified → Defined → Designed → Implemented → Validated\n\nA CREATION transition will also be created if it doesn\'t exist.\n\nContinue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post(`/prime-artifacts/${currentArtifact.id}/apply-default-states`, {
        include_operational: true,
        include_improvement: true
      });
      
      // Refresh the artifact data to get the new states and transitions
      await Promise.all([
        fetchStatesRef.current(localArtifact),
        fetchTransitionsRef.current(localArtifact),
      ]);
      
      alert(`Default states applied successfully! Created ${response.data.state_count} states and ${response.data.transition_count} transitions.`);
    } catch (error: any) {
      console.error('Error applying default states:', error);
      alert(error.response?.data?.detail || 'Failed to apply default states');
    } finally {
      setLoading(false);
    }
  };

  // Handle create state
  const handleCreateState = async () => {
    if (!newStateName.trim()) {
      alert('Please enter a state name');
      return;
    }

    try {
      setLoading(true);
      const newState = await PrimeArtifactService.createPrimeArtifactState({
        name: newStateName.trim(),
        description: newStateDescription.trim() || undefined,
        prime_artifact_ref: currentArtifact.id,
        state_type: newStateType,
        is_initial: newStateIsInitial,
        is_final: newStateIsFinal,
        is_required: newStateIsRequired,
        is_improvement: false,
        is_resource_active: false,
        demand_type: DemandType.SUPPLY,
        demand_flow_type: DemandFlowType.PULL,
        state_metadata: {},
        state_rules: {},
        state_parameters: {},
        validation_rules: {},
      });

      // Update local artifact with new state
      setLocalArtifact(prev => ({
        ...prev,
        states: [...(prev.states || []), newState]
      }));

      // Reset form and close
      setNewStateName('');
      setNewStateDescription('');
      setNewStateType(ArtifactStateTransitionType.OPERATIONAL);
      setNewStateIsInitial(false);
      setNewStateIsFinal(false);
      setNewStateIsRequired(true);
      setShowAddState(false);
      
      // Optionally select the new state
      setSelectedState(newState);
    } catch (error) {
      console.error('Error creating state:', error);
      alert('Failed to create state. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-select first process when processes load
  useEffect(() => {
    if (currentArtifact.processes && currentArtifact.processes.length > 0 && !selectedProcess) {
      setSelectedProcess(currentArtifact.processes[0]);
    }
  }, [currentArtifact.processes, selectedProcess]);

  const handleEditProcess = (processId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent selecting the process
    setEditingProcessId(processId);
    setShowEditProcessDialog(true);
  };

  const handleCloseEditProcessDialog = () => {
    setShowEditProcessDialog(false);
    setEditingProcessId(null);
    // Refresh processes after edit
    void fetchProcessesRef.current(currentArtifact);
  };

  const handleDeleteProcess = (processId: string, processName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent selecting the process
    
    if (!window.confirm(`Are you sure you want to delete the process "${processName}"? This will:\n\n- Remove all artifact associations\n- Clear process references from transitions\n\nThis action cannot be undone.`)) {
      return;
    }

    apiClient.delete(`/prime-artifacts/${currentArtifact.id}/processes/${processId}`)
      .then(() => {
        // Clear selection if deleted process was selected
        if (selectedProcess?.id === processId) {
          setSelectedProcess(null);
        }
        // Refresh processes
        void fetchProcessesRef.current(currentArtifact);
        // Show success message
        alert('Process deleted successfully');
      })
      .catch((error: any) => {
        console.error('Error deleting process:', error);
        alert(`Failed to delete process: ${error.response?.data?.detail || error.message}`);
      });
  };

  // Toggle handlers for approval/rejection
  const handleToggleDetailsApproval = async (newState: boolean) => {
    try {
      if (newState) {
        await approveDetails();
      } else {
        await PrimeArtifactService.rejectDetails(currentArtifact.id);
      }
      const updatedArtifact = await PrimeArtifactService.getById(currentArtifact.id);
      setLocalArtifact(updatedArtifact);
    } catch (error) {
      console.error('Error toggling details approval:', error);
      throw error;
    }
  };

  const handleToggleStatesApproval = async (newState: boolean) => {
    try {
      if (newState) {
        await approveStates();
      } else {
        await PrimeArtifactService.rejectStates(currentArtifact.id);
      }
      const updatedArtifact = await PrimeArtifactService.getById(currentArtifact.id);
      setLocalArtifact(updatedArtifact);
    } catch (error) {
      console.error('Error toggling states approval:', error);
      throw error;
    }
  };

  // const handleToggleTransitionsApproval = async (newState: boolean) => {
  //   try {
  //     if (newState) {
  //       await approveTransitions();
  //     } else {
  //       await PrimeArtifactService.rejectTransitions(currentArtifact.id);
  //     }
  //     const updatedArtifact = await PrimeArtifactService.getById(currentArtifact.id);
  //     setLocalArtifact(updatedArtifact);
  //   } catch (error) {
  //     console.error('Error toggling transitions approval:', error);
  //     throw error;
  //   }
  // };

  const handleToggleProcessesApproval = async (newState: boolean) => {
    try {
      if (newState) {
        await approveProcesses();
      } else {
        await PrimeArtifactService.rejectProcesses(currentArtifact.id);
      }
      const updatedArtifact = await PrimeArtifactService.getById(currentArtifact.id);
      setLocalArtifact(updatedArtifact);
    } catch (error) {
      console.error('Error toggling processes approval:', error);
      throw error;
    }
  };

  // Sidebar resize handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX;
    const minWidth = 200;
    const maxWidth = 600;  // Increased from 500 for deeper trees
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setSidebarWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      // Save the final width to localStorage
      localStorage.setItem('primeModelSidebarWidth', sidebarWidth.toString());
      // Sidebar width saved
    }
    setIsResizing(false);
  }, [isResizing, sidebarWidth]);
  
  // Handle diagram collapse toggle
  const handleDiagramCollapseToggle = useCallback(() => {
    const newCollapsed = !diagramCollapsed;
    setDiagramCollapsed(newCollapsed);
    localStorage.setItem('primeModelDiagramCollapsed', newCollapsed.toString());
    // Diagram collapse state saved
  }, [diagramCollapsed]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Update local artifact when prop changes
  useEffect(() => {
    setLocalArtifact(_artifact);
    // Reset selection to artifact when artifact changes
    setSelectedElement({ type: 'artifact', id: _artifact.id });
  }, [_artifact]);
  
  // Load states and transitions for diagram
  const loadDiagramData = useCallback(async () => {
    try {
      // Removed verbose diagram loading log
      const [statesData, transitionsData, processesData] = await Promise.all([
        PrimeArtifactService.getPrimeArtifactStates(currentArtifact.id),
        apiClient.get(`/prime-transitions/${currentArtifact.id}`),
        PrimeArtifactService.getPrimeArtifactProcesses(currentArtifact.id)
      ]);
      
      setStatesForDiagram(statesData);
      
      // Build process lookup map for quick access by ID
      const processMap = new Map(processesData.map(p => [p.id, p]));
      
      // Transform transitions for diagram
      const diagramTransitions = transitionsData.data.map((t: any) => {
        // Look up the actual process name if there's a process reference
        const process = t.prime_process_ref ? processMap.get(t.prime_process_ref) : null;
        
        // Helper to normalize state ID (handle None/null/empty)
        const normalizeStateId = (id: any) => {
          if (!id || id === 'None' || id === 'null' || id === '') return '';
          return String(id);
        };
        
        // Use nested state objects if available, otherwise fall back to ref fields
        // Also handle the explicit from_state_id/to_state_id fields from API
        const from_state_id = normalizeStateId(
          t.from_state?.id || t.from_state_id || t.from_state_ref
        );
        const to_state_id = normalizeStateId(
          t.to_state?.id || t.to_state_id || t.to_state_ref
        );
        const from_state_name = t.from_state?.name || t.from_state_name || (from_state_id ? 'Unknown' : '');
        const to_state_name = t.to_state?.name || t.to_state_name || (to_state_id ? 'Unknown' : '');
        
        return {
          id: t.id,
          name: t.name,
          from_state_id: from_state_id,
          to_state_id: to_state_id,
          from_state_name: from_state_name,
          to_state_name: to_state_name,
          transition_role: t.default_transition_type || 'PROCESSITEM',
          process_name: process?.name || null,
          process_id: t.prime_process_ref || null, // Actual process ID
          diagram_waypoints: t.diagram_waypoints || null,
          diagram_process_x: t.diagram_process_x || null,
          diagram_process_y: t.diagram_process_y || null
        };
      });
      
      // Validate transitions have valid state references
      interface DiagramTransition {
        id: string;
        name: string;
        from_state_id: string;
        to_state_id: string;
        from_state_name: string;
        to_state_name: string;
        transition_role: string;
        process_name?: string | null;
        process_id?: string | null;
        diagram_waypoints?: Array<{x: number, y: number}> | null;
        diagram_process_x?: number | null;
        diagram_process_y?: number | null;
      }
      
      // Normalize all state IDs to strings for comparison
      const normalizedStateIds = new Set(statesData.map((s: any) => String(s.id)));
      
      const validTransitions = diagramTransitions.filter((t: DiagramTransition) => {
        // from_state_id can be empty (initial transitions) or must match a state
        const fromStateIdStr = t.from_state_id ? String(t.from_state_id) : '';
        const hasValidFrom = !fromStateIdStr || normalizedStateIds.has(fromStateIdStr);
        
        // to_state_id must exist and match a state
        const toStateIdStr = t.to_state_id ? String(t.to_state_id) : '';
        const hasValidTo = toStateIdStr && normalizedStateIds.has(toStateIdStr);
        
        if (!hasValidFrom || !hasValidTo) {
          console.warn(`[PrimeModelManager] Transition '${t.name}' has invalid state references:`, {
            from_state_id: fromStateIdStr,
            to_state_id: toStateIdStr,
            from_state_name: t.from_state_name,
            to_state_name: t.to_state_name,
            availableStateIds: Array.from(normalizedStateIds),
            stateIdTypes: statesData.map((s: any) => ({ id: s.id, type: typeof s.id })),
            transitionIdTypes: { from: typeof t.from_state_id, to: typeof t.to_state_id }
          });
        }
        return hasValidFrom && hasValidTo;
      });
      
      if (validTransitions.length !== diagramTransitions.length) {
        console.warn(`[PrimeModelManager] Filtered out ${diagramTransitions.length - validTransitions.length} transitions with invalid state references`);
      }
      
      setTransitionsForDiagram(validTransitions);
      
      // Log transition types to debug filtering issues
      const transitionTypes = validTransitions.reduce((acc: Record<string, number>, t: DiagramTransition) => {
        const type = t.transition_role || 'UNKNOWN';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      
      // Removed verbose diagram data log
    } catch (error) {
      console.error('[PrimeModelManager] Error loading diagram data:', error);
    }
  }, [currentArtifact.id]);
  
  useEffect(() => {
    void loadDiagramData();
  }, [loadDiagramData]);

  // Removed verbose mount and artifact change logs

  // Helper function to get process warnings
  const getProcessWarnings = useCallback((process: PrimeProcess): string[] => {
    const warnings: string[] = [];
    
    // Check for artifacts in process (using currentArtifact.processes data)
    // Note: This is a simplified check - full validation would require fetching process artifacts
    
    // Placeholder for now - will be enhanced when we have process artifact data in context
    // For now, just return empty array
    return warnings;
  }, []);

  // Removed verbose tab change log

  const handleTabChange = (tab: string) => {
    // Removed verbose log - tab changes are handled silently
    setActiveTab(tab);
  };

  // This will be moved after function declarations

  const fetchStates = useCallback(
    async (artifact: PrimeArtifact) => {
      if (!artifact) {
        // No artifact provided
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const states = await PrimeArtifactService.getPrimeArtifactStates(artifact.id);
        // Update local artifact with fetched states
        setLocalArtifact(prev => {
          const updated = {
            ...prev,
            states,
          };
          return updated;
        });
      } catch (err) {
        console.error('[PrimeModelManager] Error fetching states:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch states');
      } finally {
        setLoading(false);
      }
    },
    [selectArtifact]
  );

  const fetchTransitions = useCallback(
    async (artifact: PrimeArtifact) => {
      if (!artifact) return;
      // Removed verbose fetch log
      setLoading(true);
      setError(null);
      try {
        const transitions = await PrimeArtifactService.getPrimeArtifactTransitions(artifact.id);
        // Update local artifact with fetched transitions
        setLocalArtifact(prev => {
          const updated = {
            ...prev,
            transitions,
          };
          return updated;
        });
      } catch (err) {
        console.error('[PrimeModelManager] Error fetching transitions:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch transitions');
      } finally {
        setLoading(false);
      }
    },
    [selectArtifact]
  );

  const fetchProcesses = useCallback(
    async (artifact: PrimeArtifact) => {
      if (!artifact) return;
      // Removed verbose fetch log
      setLoading(true);
      setError(null);
      try {
        const processes = await PrimeArtifactService.getPrimeArtifactProcesses(artifact.id);
        // Update local artifact with fetched processes
        setLocalArtifact(prev => {
          const updated = {
            ...prev,
            processes,
          };
          return updated;
        });
      } catch (err) {
        console.error('[PrimeModelManager] Error fetching processes:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch processes');
      } finally {
        setLoading(false);
      }
    },
    [selectArtifact]
  );

  // Store fetch functions in refs to avoid dependency issues
  const fetchStatesRef = useRef(fetchStates);
  const fetchTransitionsRef = useRef(fetchTransitions);
  const fetchProcessesRef = useRef(fetchProcesses);

  // Update refs when functions change
  useEffect(() => {
    fetchStatesRef.current = fetchStates;
    fetchTransitionsRef.current = fetchTransitions;
    fetchProcessesRef.current = fetchProcesses;
  }, [fetchStates, fetchTransitions, fetchProcesses]);

  // Fetch data when component mounts or artifact ID changes (not when data updates)
  useEffect(() => {
    if (_artifact && !isFetching.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PrimeModelManager] Initial data fetch triggered for artifact:', _artifact.id);
      }
      isFetching.current = true;

      const fetchAllData = async () => {
        try {
          await Promise.all([
            fetchStatesRef.current(_artifact),
            fetchTransitionsRef.current(_artifact),
            fetchProcessesRef.current(_artifact),
          ]);
        } finally {
          isFetching.current = false;
        }
      };

      void fetchAllData();
    }
  }, [_artifact.id]); // Only re-fetch when artifact ID changes, not when local state updates

  // Fetch data when switching tabs (use _artifact to avoid loop from localArtifact updates)
  useEffect(() => {
    if (!_artifact || isFetching.current) return;

    switch (activeTab) {
      case 'states':
        void fetchStatesRef.current(_artifact);
        break;
      case 'transitions':
        void fetchTransitionsRef.current(_artifact);
        break;
      case 'processes':
        void fetchProcessesRef.current(_artifact);
        break;
      case 'process-artifacts':
        void fetchProcessesRef.current(_artifact);
        break;
    }
  }, [activeTab, _artifact.id]); // Use _artifact.id instead of currentArtifact to avoid loop

  const tabContent = () => {
    if (!currentArtifact) {
      return null;
    }

    const renderContent = () => {
      switch (activeTab) {
        case 'details':
          return currentArtifact ? (
            <ArtifactDetailEditor
              artifactId={currentArtifact.id}
              onSave={async () => {
                await fetchStatesRef.current(currentArtifact);
              }}
            />
          ) : null;
        case 'states':
          return (
            <div className={styles.statesContent}>
              {/* Three-column layout */}
              <div className={styles.statesLayout}>
                {/* Column 1: State List */}
                <div className={styles.statesListSection}>
                  <div className={styles.statesListHeader}>
                    <h3>States</h3>
                    <div className={styles.statesListActions}>
                      {tabValidation?.states && (
                        <button 
                          className={styles.approveButton}
                          onClick={approveStates}
                        >
                          Approve
                        </button>
                      )}
                      <button 
                        className={styles.resetButton}
                        onClick={handleResetToTemplate}
                        disabled={!currentArtifact.artifact_type_id}
                        title={!currentArtifact.artifact_type_id ? "This artifact is not linked to an artifact type" : "Reset states and transitions to artifact type template"}
                      >
                        Reset
                      </button>
                      <button 
                        className={styles.applyDefaultButton}
                        onClick={handleApplyDefaultStates}
                        title="Apply default operational and improvement states (preserves existing states)"
                      >
                        Apply Defaults
                      </button>
                      {!userLoading && isAuthenticated && hasPermission(PERMISSIONS.WRITE_ARTIFACTS) && (
                        <button 
                          className={styles.addButton}
                          onClick={() => setShowAddState(true)}
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                  <div className={styles.statesList}>
                    {(currentArtifact.states || [])
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((state) => (
                      <div
                        key={state.id}
                        className={`${styles.stateItem} ${selectedState?.id === state.id ? styles.selected : ''}`}
                        onClick={() => setSelectedState(state)}
                      >
                        <div className={styles.stateName}>{state.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Column 2: State Details */}
                <div className={styles.statesDetailsSection}>
                  {selectedState ? (
                    <StateDetailEditor
                      stateId={selectedState.id}
                      artifactId={currentArtifact.id}
                      onSave={async () => {
                        await fetchStatesRef.current(currentArtifact);
                      }}
                    />
                  ) : (
                    <div className={styles.noStateSelected}>
                      <p>Select a state to view its details</p>
                    </div>
                  )}
                </div>
                
                {/* Column 3: State Properties */}
                <div className={styles.statesPropertiesSection}>
                  {selectedState ? (
                    <StatePropertyManager
                      stateId={selectedState.id}
                      stateName={selectedState.name}
                      onPropertyUpdated={() => {
                        console.log('Properties updated for state:', selectedState.name);
                      }}
                    />
                  ) : (
                    <div className={styles.noStateSelected}>
                      <p>Select a state to manage its properties</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        case 'creation-relationships':
          return (
            <div className={styles.creationRelationshipsContent}>
              <h2>Creation Relationships</h2>
              <CreationRelationshipsManager
                artifactId={currentArtifact.id}
                artifactName={currentArtifact.name}
              />
            </div>
          );
        case 'parameters':
          return (
            <div className={styles.parametersContent}>
              <Alert
                type="info"
                showIcon
                message="Use Global properties instead"
                description={
                  <span>
                    Prime property types are defined on{' '}
                    <Link to="/admin/global-properties">
                      Platform administration → Global properties
                    </Link>
                    . Artifact properties for this prime are on the Properties tab.{' '}
                    <Button type="link" onClick={() => handleTabChange('properties')}>
                      Open Properties
                    </Button>
                  </span>
                }
              />
            </div>
          );
        case 'properties':
          return (
            <div className={styles.propertiesContent}>
              <PrimeArtifactPropertiesManager
                artifactId={currentArtifact.id}
                artifactName={currentArtifact.name}
                onPropertiesUpdated={() => {
                  console.log('Properties updated for artifact:', currentArtifact.name);
                }}
              />
            </div>
          );
        case 'transitions-list':
          return (
            <div className={styles.transitionsListContent}>
              <h2>Transitions & Processes</h2>
              <TransitionsProcessesView
                artifactId={currentArtifact.id}
                artifactName={currentArtifact.name}
                hideDiagram={true}
                onEditTransition={(transitionId) => {
                  handleTransitionClick(transitionId);
                }}
                onEditProcess={(processId) => {
                  handleProcessClick(processId);
                }}
              />
            </div>
          );
        case 'state-details':
          // Only render state editor if a state is actually selected
          if (selectedElement.type !== 'state') {
            return (
              <div className={styles.stateDetailsContent}>
                <div className={styles.noSelectionMessage}>
                  <p>Please select a state from the diagram to view its details.</p>
                </div>
              </div>
            );
          }
          return (
            <div className={styles.stateDetailsContent}>
              <StateDetailEditor
                stateId={selectedElement.id}
                artifactId={currentArtifact.id}
                onSave={async () => {
                  await fetchStatesRef.current(currentArtifact);
                  // Reload diagram data to include updated positions
                  await loadDiagramData();
                }}
              />
            </div>
          );
        case 'state-properties': {
          if (selectedElement.type !== 'state') {
            return (
              <div className={styles.statePropertiesContent}>
                <div className={styles.noSelectionMessage}>
                  <p>Please select a state from the diagram to view its properties.</p>
                </div>
              </div>
            );
          }
          // Find the state name from the artifact states
          const state = currentArtifact.states?.find(s => s.id === selectedElement.id);
          const stateName = state?.name || 'State';
          
          return (
            <div className={styles.statePropertiesContent}>
              <StatePropertyManager
                stateId={selectedElement.id}
                stateName={stateName}
                onPropertyUpdated={() => {
                  console.log('Properties updated for state:', selectedElement.id);
                }}
              />
            </div>
          );
        }
        case 'transition-basic':
        case 'transition-parameters':
        case 'transition-validation':
        case 'transition-rollup':
        case 'transition-generation':
          // Only render transition editor if a transition is actually selected
          if (selectedElement.type !== 'transition') {
            return (
              <div className={styles.transitionDetailsContent}>
                <div className={styles.noSelectionMessage}>
                  <p>Please select a transition from the diagram to view its details.</p>
                </div>
              </div>
            );
          }
          return (
            <div className={styles.transitionDetailsContent}>
              <TransitionDetailEditor
                transitionId={selectedElement.id}
                artifactId={currentArtifact.id}
                activeTab={activeTab}
                onSave={async () => {
                  // Reload diagram data to include updated positions
                  await loadDiagramData();
                }}
              />
            </div>
          );
        case 'process-basic':
        case 'process-duration':
        case 'process-artifacts-detail':
          // Only render process editor if a process is actually selected
          if (selectedElement.type !== 'process') {
            return (
              <div className={styles.processDetailsContent}>
                <div className={styles.noSelectionMessage}>
                  <p>Please select a process from the diagram or list to view its details.</p>
                </div>
              </div>
            );
          }
          return (
            <div className={styles.processDetailsContent}>
              <ProcessDetailEditor
                processId={selectedElement.id}
                artifactId={currentArtifact.id}
                activeTab={activeTab}
                onSave={async () => {
                  await fetchProcessesRef.current(currentArtifact);
                }}
              />
            </div>
          );
        case 'transitions':
          return (
            <div className={styles.transitionsContent}>
              <h2>Transitions & Processes</h2>
              {tabValidation?.transitions && (
                <TabValidationStatus
                  tabName="Transitions"
                  validation={tabValidation.transitions}
                  onApprove={approveTransitions}
                />
              )}
              <Collapse 
                ghost
                items={[
                  {
                    key: '1',
                    label: 'Transition Process Generator (Auto-generate processes for transitions without processes)',
                    children: <TransitionProcessGenerator artifactId={currentArtifact.id} />,
                  },
                ]}
              />
              <div style={{ marginTop: 24 }}>
                <TransitionsProcessesView
                  artifactId={currentArtifact.id}
                  artifactName={currentArtifact.name}
                  onEditTransition={(transitionId) => {
                    // Could open a modal or navigate to edit view
                    console.log('Edit transition:', transitionId);
                  }}
                  onEditProcess={(processId) => {
                    // Could open a modal or navigate to edit view
                    console.log('Edit process:', processId);
                  }}
                />
              </div>
            </div>
          );
        case 'process-artifacts':
          return (
            <div className={styles.processArtifactsContent}>
              {currentArtifact.processes && currentArtifact.processes.length > 0 ? (
                <div className={styles.processArtifactsLayout}>
                  <div className={styles.processList}>
                    <div className={styles.processListHeader}>
                      <h3>Processes ({currentArtifact.processes.length})</h3>
                    </div>
                    <div className={styles.processListItems}>
                      {currentArtifact.processes.map((process: PrimeProcess) => {
                        const warnings = getProcessWarnings(process);
                        return (
                          <div
                            key={process.id}
                            className={`${styles.processListItem} ${selectedProcess?.id === process.id ? styles.selected : ''}`}
                            onClick={() => setSelectedProcess(process)}
                          >
                            <div className={styles.processListItemContent}>
                              <div className={styles.processListItemName}>
                                {process.name}
                                {warnings.length > 0 && (
                                  <Tooltip 
                                    title={
                                      <div>
                                        {warnings.map((w, idx) => (
                                          <div key={idx}>• {w}</div>
                                        ))}
                                      </div>
                                    }
                                  >
                                    <WarningOutlined style={{ color: '#faad14', marginLeft: 8, fontSize: 14 }} />
                                  </Tooltip>
                                )}
                              </div>
                              <div className={styles.processListItemType}>{process.process_type}</div>
                            </div>
                            <div className={styles.processListItemActions}>
                              <button
                                className={styles.processEditButton}
                                onClick={(e) => handleEditProcess(process.id, e)}
                                title="Edit process"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                              </button>
                              <button
                                className={styles.processDeleteButton}
                                onClick={(e) => handleDeleteProcess(process.id, process.name, e)}
                                title="Delete process"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  <line x1="10" y1="11" x2="10" y2="17"></line>
                                  <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className={styles.processDetail}>
                    {selectedProcess ? (
                      <ProcessArtifactManager
                        processId={selectedProcess.id}
                        processName={selectedProcess.name}
                        onUpdate={() => void fetchProcessesRef.current(currentArtifact)}
                      />
                    ) : (
                      <div className={styles.noProcessSelected}>
                        <p>Select a process from the list to manage its artifacts</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
                  No processes for this artifact. Create transitions first, then generate processes.
                </div>
              )}
            </div>
          );
        case 'krybot-agent':
          return (
            <div className={styles.krybotAgentContent} style={{ padding: '0', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <PrimeAgentChat
                artifactId={currentArtifact.id}
                artifactName={currentArtifact.name}
                userId={user?.id || ''}
                onApprovalDecision={async () => {
                  // Refresh artifact data after approval
                  try {
                    const updatedArtifact = await PrimeArtifactService.getById(currentArtifact.id);
                    setLocalArtifact(updatedArtifact);
                    selectArtifact(updatedArtifact);
                    await dispatch(fetchPrimeArtifactsHierarchy());
                    // Reload states, transitions, and processes after approval
                    await fetchStatesRef.current(currentArtifact);
                    await fetchTransitionsRef.current(currentArtifact);
                    await fetchProcessesRef.current(currentArtifact);
                  } catch (error) {
                    console.error('Error refreshing artifact after approval:', error);
                  }
                }}
              />
            </div>
          );
        case 'workflow':
          return (
            <div className={styles.workflowContent}>
              <h2>Workflow Status</h2>
              <WorkflowStatusComponent
                card={{
                  id: currentArtifact.id,
                  type: 'artifact' as any,
                  data: {
                    artifact: currentArtifact,
                    artifactId: currentArtifact.id
                  } as any,
                  context: {
                    uiRole: 'architect' as any,
                    modelType: 'Prime' as any,
                    threadId: 'default',
                    userId: 'current-user',
                    dataSource: 'live' as any
                  },
                  layout: {
                    baseLayout: 'expanded' as any,
                    styling: {
                      backgroundColor: '#fff',
                      borderColor: '#d9d9d9',
                      borderWidth: 1,
                      borderRadius: 8,
                      padding: 16,
                      shadow: 'sm'
                    } as any, 
                    sections: [] 
                  },
                  metadata: {
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    createdBy: 'system',
                    version: 1
                  } as any,
                  location: 'center' as any,
                  triggers: []
                }}
                section={{
                  id: 'workflow-status',
                  type: 'custom' as any,
                  roleVisibility: ['architect'] as any,
                  modelVisibility: ['Prime'] as any,
                  component: 'workflow-status',
                  order: 1
                }}
                onAction={async (action, payload) => {
                  console.log('Workflow action:', action, payload);
                  if (action === 'workflow-advanced') {
                    // Reload artifact data from backend to get updated development_status
                    try {
                      const updatedArtifact = await PrimeArtifactService.getById(currentArtifact.id);
                      setLocalArtifact(updatedArtifact);
                      // Also update in context to refresh tree
                      selectArtifact(updatedArtifact);
                      // Refresh the hierarchy in Redux store to update tree display
                      await dispatch(fetchPrimeArtifactsHierarchy());
                    } catch (error) {
                      console.error('Error refreshing artifact after workflow advancement:', error);
                    }
                  }
                }}
              />
              
              <div style={{ marginTop: '24px' }}>
                <PrimeModelTester
                  artifactId={currentArtifact.id}
                  artifactName={currentArtifact.name}
                  onTestComplete={(results) => {
                    console.log('Prime model test completed:', results);
                    // Optionally update artifact status based on test results
                    if (results.overall_success) {
                      console.log('Model passed all tests - ready for production use');
                    } else {
                      console.log('Model has issues that need to be addressed:', results.recommendations);
                    }
                  }}
                />
              </div>
            </div>
          );
        default:
          console.log('[PrimeModelManager] Unknown tab:', activeTab);
          return null;
      }
    };

    return (
      <div className={`${styles.tabContent} ${activeTab === 'process-artifacts' ? styles.processArtifactsTab : ''}`}>
        {loading && <div className={styles.loading}>Loading...</div>}
        {error && <div className={styles.error}>{error}</div>}
        {!loading && !error && renderContent()}
      </div>
    );
  };

  // Build artifact tree data with custom title rendering
  const buildTreeData = (artifacts: PrimeArtifact[]): any[] => {
    if (!artifacts || artifacts.length === 0) {
      return [];
    }
    
    return artifacts.map((artifact: PrimeArtifact) => {
      const hasChildren = artifact.children && artifact.children.length > 0;
      
      return {
      key: artifact.id,
      title: (
        <div className={styles.artifactTreeNodeTitle}>
          <span className={styles.artifactTreeName}>{artifact.name}</span>
          {artifact.development_status && (
            <span className={`${styles.treeStatusBadge} ${
              artifact.development_status === 'IDENTIFIED' ? styles.statusIdentified :
              artifact.development_status === 'DEFINED' ? styles.statusDefined :
              artifact.development_status === 'DESIGNED' ? styles.statusDesigned :
              artifact.development_status === 'IMPLEMENTED' ? styles.statusImplemented :
              styles.statusValidated
            }`}>
              {artifact.development_status === 'IDENTIFIED' ? 'ID' : 
               artifact.development_status === 'DEFINED' ? 'DEF' : 
               artifact.development_status === 'DESIGNED' ? 'DSG' :
               artifact.development_status === 'IMPLEMENTED' ? 'IMP' : 'VAL'}
            </span>
          )}
          <div className={styles.artifactTreeActions}>
            <button
              className={`${styles.treeActionBtn} ${styles.addChild}`}
              onClick={(e) => {
                e.stopPropagation();
                setDialogMode('child');
                setDialogParent({ id: artifact.id, name: artifact.name });
                setShowAddDialog(true);
              }}
              title="Add child artifact"
            >
              +
            </button>
            <button
              className={`${styles.treeActionBtn} ${styles.addPeer}`}
              onClick={(e) => {
                e.stopPropagation();
                setDialogMode('peer');
                setDialogParent({ id: artifact.parent_ref || null, name: artifact.parent_name || null });
                setShowAddDialog(true);
              }}
              title="Add peer artifact"
            >
              =
            </button>
            <button
              className={`${styles.treeActionBtn} ${styles.delete}`}
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Delete artifact "${artifact.name}"?`)) {
                  void dispatch(deletePrimeArtifact(artifact.id));
                }
              }}
              title="Delete artifact"
            >
              ×
            </button>
          </div>
        </div>
      ),
      artifact: artifact,
      children: artifact.children ? buildTreeData(artifact.children) : []
    };
    });
  };
  
  // Filter tree data by search term
  const filterTreeData = (nodes: any[], searchTerm: string): any[] => {
    if (!searchTerm) return nodes;
    
    const lowercaseSearch = searchTerm.toLowerCase();
    
    return nodes.filter((node) => {
      // Search using artifact name instead of title (which is now a React element)
      const matches = node.artifact?.name?.toLowerCase().includes(lowercaseSearch) || false;
      const childrenMatch = node.children && node.children.length > 0;
      
      if (childrenMatch) {
        node.children = filterTreeData(node.children, searchTerm);
      }
      
      return matches || (node.children && node.children.length > 0);
    });
  };
  
  // Removed verbose hierarchy debug logs

  const treeData = buildTreeData(artifactsHierarchy);
  const filteredTreeData = filterTreeData(treeData, artifactSearchTerm);
  
  // Handle artifact selection from tree
  const handleArtifactSelect = (selectedKeys: React.Key[]) => {
    if (selectedKeys.length === 0) return;
    
    const findArtifactById = (artifacts: PrimeArtifact[], id: string): PrimeArtifact | null => {
      for (const artifact of artifacts) {
        if (artifact.id === id) return artifact;
        if (artifact.children) {
          const found = findArtifactById(artifact.children, id);
          if (found) return found;
        }
      }
      return null;
    };
    
    const selectedId = selectedKeys[0] as string;
    const artifact = findArtifactById(artifactsHierarchy, selectedId);
    if (artifact) {
      selectArtifact(artifact);
    }
  };
  
  // Diagram element click handlers
  const handleStateClick = (stateId: string) => {
    setSelectedElement({ type: 'state', id: stateId });
    setActiveTab('state-details'); // Switch to state details tab
  };
  
  const handleTransitionClick = (transitionId: string) => {
    console.log('[PrimeModelManager] Transition clicked:', transitionId);
    setSelectedElement({ type: 'transition', id: transitionId });
    setActiveTab('transition-basic'); // Switch to transition tab
    console.log('[PrimeModelManager] Selection and tab updated');
  };
  
  const handleProcessClick = (processId: string) => {
    setSelectedElement({ type: 'process', id: processId });
    setActiveTab('process-basic'); // Switch to process tab
  };
  
  // Render context-aware tabs based on selection type
  const renderContextAwareTabs = () => {
    switch (selectedElement.type) {
      case 'artifact':
        return (
          <>
            <button className={`${styles.tabButton} ${activeTab === 'krybot-agent' ? styles.active : ''}`} onClick={() => handleTabChange('krybot-agent')}>
              Krybot Agent
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'details' ? styles.active : ''}`} onClick={() => handleTabChange('details')}>
              Details
              <ApprovalToggle
                isApproved={currentArtifact.details_approved || false}
                onToggle={handleToggleDetailsApproval}
              />
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'states' ? styles.active : ''}`} onClick={() => handleTabChange('states')}>
              States
              <ApprovalToggle
                isApproved={currentArtifact.states_approved || false}
                onToggle={handleToggleStatesApproval}
              />
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'properties' ? styles.active : ''}`} onClick={() => handleTabChange('properties')}>
              Properties
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'transitions-list' ? styles.active : ''}`} onClick={() => handleTabChange('transitions-list')}>
              Transitions (List)
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'process-artifacts' ? styles.active : ''}`} onClick={() => handleTabChange('process-artifacts')}>
              Process Artifacts
              <ApprovalToggle
                isApproved={currentArtifact.processes_approved || false}
                onToggle={handleToggleProcessesApproval}
              />
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'creation-relationships' ? styles.active : ''}`} onClick={() => handleTabChange('creation-relationships')}>
              Creation Relationships
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'workflow' ? styles.active : ''}`} onClick={() => handleTabChange('workflow')}>
              Workflow
            </button>
          </>
        );
      
      case 'state':
        return (
          <>
            <button className={`${styles.tabButton} ${activeTab === 'state-details' ? styles.active : ''}`} onClick={() => handleTabChange('state-details')}>
              State Details
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'state-properties' ? styles.active : ''}`} onClick={() => handleTabChange('state-properties')}>
              Properties
            </button>
            <button className={styles.tabButton} onClick={() => { setSelectedElement({ type: 'artifact', id: currentArtifact.id }); handleTabChange('details'); }}>
              ← Back to Artifact
            </button>
          </>
        );
      
      case 'transition':
        return (
          <>
            <button className={`${styles.tabButton} ${activeTab === 'transition-basic' ? styles.active : ''}`} onClick={() => handleTabChange('transition-basic')}>
              Basic Info
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'transition-parameters' ? styles.active : ''}`} onClick={() => handleTabChange('transition-parameters')}>
              Parameters
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'transition-validation' ? styles.active : ''}`} onClick={() => handleTabChange('transition-validation')}>
              Validation Rules
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'transition-rollup' ? styles.active : ''}`} onClick={() => handleTabChange('transition-rollup')}>
              Rollup Config
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'transition-generation' ? styles.active : ''}`} onClick={() => handleTabChange('transition-generation')}>
              Artifact Generation
            </button>
            <button className={styles.tabButton} onClick={() => { setSelectedElement({ type: 'artifact', id: currentArtifact.id }); handleTabChange('transitions-list'); }}>
              ← Back to List
            </button>
          </>
        );
      
      case 'process':
        return (
          <>
            <button className={`${styles.tabButton} ${activeTab === 'process-basic' ? styles.active : ''}`} onClick={() => handleTabChange('process-basic')}>
              Basic Info
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'process-duration' ? styles.active : ''}`} onClick={() => handleTabChange('process-duration')}>
              Duration & Execution
            </button>
            <button className={`${styles.tabButton} ${activeTab === 'process-artifacts-detail' ? styles.active : ''}`} onClick={() => handleTabChange('process-artifacts-detail')}>
              Process Artifacts
            </button>
            <button className={styles.tabButton} onClick={() => { setSelectedElement({ type: 'artifact', id: currentArtifact.id }); handleTabChange('process-artifacts'); }}>
              ← Back to List
            </button>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div 
      className={styles.primeModelManagerLayout}
      style={{
        gridTemplateColumns: sidebarCollapsed ? '40px 1fr' : `${sidebarWidth}px 1fr`
      }}
    >
      {/* Left Sidebar: Artifact Tree */}
      <div 
        className={`${styles.artifactTreeSidebar} ${sidebarCollapsed ? styles.collapsed : ''} ${isResizing ? styles.resizing : ''}`}
      >
        {!sidebarCollapsed && (
          <>
            <div className={styles.sidebarHeader}>
              <h3>Prime Artifacts</h3>
              <div style={{ display: 'flex', gap: '4px' }}>
                {(() => {
                  const canAdd = !userLoading && isAuthenticated && hasPermission(PERMISSIONS.WRITE_ARTIFACTS);
                  
                  return canAdd ? (
                    <button 
                      className={styles.addRootButton}
                      onClick={() => {
                        setDialogMode('root');
                        setDialogParent({ id: null, name: null });
                        setShowAddDialog(true);
                      }}
                      title="Add root artifact"
                    >
                      <PlusOutlined />
                    </button>
                  ) : null;
                })()}
                <button 
                  className={styles.collapseButton}
                  onClick={() => setSidebarCollapsed(true)}
                  title="Collapse sidebar"
                >
                  <ChevronLeft size={18} />
                </button>
              </div>
            </div>
            <Search
              placeholder="Search artifacts..."
              value={artifactSearchTerm}
              onChange={(e) => setArtifactSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              style={{ marginBottom: '12px' }}
            />
            <Tree
              treeData={filteredTreeData}
              selectedKeys={[currentArtifact.id]}
              onSelect={handleArtifactSelect}
              showLine
              defaultExpandAll={!!artifactSearchTerm}
            />
          </>
        )}
        {sidebarCollapsed && (
          <button
            className={styles.expandButton}
            onClick={() => setSidebarCollapsed(false)}
            title="Expand sidebar"
          >
            <ChevronRight size={18} />
          </button>
        )}
        
        {/* Resize Handle - separate from sidebar content */}
        {!sidebarCollapsed && (
          <div 
            className={styles.sidebarResizeHandle}
            onMouseDown={handleMouseDown}
            title="Drag to resize sidebar"
          />
        )}
      </div>
      
      {/* Main Diagram Area - Pure Flowchart */}
      <div className={`${styles.diagramMainArea} ${diagramCollapsed ? styles.collapsed : ''}`}>
        <ArtifactStateFlowDiagram
          states={statesForDiagram}
          transitions={transitionsForDiagram}
          artifactName={currentArtifact.name}
          artifactId={currentArtifact.id}
          onStateClick={handleStateClick}
          onTransitionClick={handleTransitionClick}
          onProcessClick={handleProcessClick}
          onArtifactClick={() => {
            setSelectedElement({ type: 'artifact', id: currentArtifact.id });
            setActiveTab('details');
          }}
          selectedElement={selectedElement}
          showFilters={true}
          visibleTransitionTypes={visibleTransitionTypes}
          onFilterChange={setVisibleTransitionTypes}
          onDiagramUpdate={loadDiagramData}
          collapsed={diagramCollapsed}
          onCollapseToggle={handleDiagramCollapseToggle}
        />
      </div>
      
      {/* Bottom Tabs Area - Context Aware */}
      <div className={styles.tabsBottomArea}>
        <div className={styles.tabs}>
          {renderContextAwareTabs()}
        </div>
        {tabContent()}
      </div>

      {/* Edit Process Dialog */}
      <EditProcessDialog
        visible={showEditProcessDialog}
        processId={editingProcessId}
        artifactId={currentArtifact.id}
        onClose={handleCloseEditProcessDialog}
        onSave={handleCloseEditProcessDialog}
      />
      
      {/* Artifact Creation Dialog */}
      <ArtifactCreationDialog
        isOpen={showAddDialog}
        isChild={dialogMode === 'child'}
        parentId={dialogParent.id}
        parentName={dialogParent.name}
        onClose={async () => {
          setShowAddDialog(false);
          setDialogMode('root');
          setDialogParent({ id: null, name: null });
          // Refresh hierarchy after creation
          await dispatch(fetchPrimeArtifactsHierarchy());
        }}
      />

      {/* Add State Modal */}
      {showAddState && (
        <div className={styles.modalOverlay} onClick={() => setShowAddState(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Create New State</h3>
            <div className={styles.formGroup}>
              <label htmlFor="state-name">Name *</label>
              <input
                id="state-name"
                type="text"
                value={newStateName}
                onChange={(e) => setNewStateName(e.target.value)}
                placeholder="Enter state name"
                autoFocus
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="state-description">Description</label>
              <textarea
                id="state-description"
                value={newStateDescription}
                onChange={(e) => setNewStateDescription(e.target.value)}
                placeholder="Enter state description"
                rows={3}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="state-type">State Type *</label>
              <select
                id="state-type"
                value={newStateType}
                onChange={(e) => setNewStateType(e.target.value as ArtifactStateTransitionType)}
              >
                <option value={ArtifactStateTransitionType.OPERATIONAL}>Operational</option>
                <option value={ArtifactStateTransitionType.MATERIAL}>Material</option>
                <option value={ArtifactStateTransitionType.RESOURCE}>Resource</option>
                <option value={ArtifactStateTransitionType.CREATION}>Creation</option>
                <option value={ArtifactStateTransitionType.IMPROVEMENT}>Improvement</option>
              </select>
            </div>
            
            <div className={`${styles.formGroup} ${styles.checkboxGroup}`}>
              <div className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id="is-initial"
                  checked={newStateIsInitial}
                  onChange={(e) => setNewStateIsInitial(e.target.checked)}
                />
                <label htmlFor="is-initial">Initial State</label>
              </div>
              <div className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id="is-final"
                  checked={newStateIsFinal}
                  onChange={(e) => setNewStateIsFinal(e.target.checked)}
                />
                <label htmlFor="is-final">Final State</label>
              </div>
              <div className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id="is-required"
                  checked={newStateIsRequired}
                  onChange={(e) => setNewStateIsRequired(e.target.checked)}
                />
                <label htmlFor="is-required">Required</label>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button 
                className={styles.cancelButton} 
                onClick={() => setShowAddState(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className={styles.createButton} 
                onClick={handleCreateState}
                disabled={loading || !newStateName.trim()}
              >
                {loading ? 'Creating...' : 'Create State'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

PrimeModelManager.displayName = 'PrimeModelManager';

export default PrimeModelManager;
